import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"
import { Action, combineReducers, configureStore, ThunkAction, UnknownAction } from "@reduxjs/toolkit"
import { persistReducer, persistStore } from "redux-persist"
import { PersistPartial } from "redux-persist/es/persistReducer"
import storage from "redux-persist/lib/storage" // Defaults to localStorage for web

import { listenerMiddleware } from "@/store/middleware/listenerMiddleware.ts"

import campaignCreationReducer from "./campaign/campaignCreationSlice"
import campaignSearchReducer from "./campaign/campaignSearchSlice"
// Import your slices and middleware
import { counterSlice } from "./counter/counterSlice"
import { apiSlice } from "./rtk-api/apiSlice"
import userReducer from "./user/userSlice"

// Action dispatched on logout to reset every slice back to its initial state.
// Pair this with `persistor.purge()` to also wipe the localStorage snapshot.
export const RESET_ALL_STATE = "store/resetAll"
export const resetAllState = () => ({ type: RESET_ALL_STATE as typeof RESET_ALL_STATE })

// Combine reducers
export const rootReducer = combineReducers({
  counter: counterSlice.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  user: userReducer,
  campaignCreation: campaignCreationReducer,
  campaignSearch: campaignSearchReducer,
})

// Wraps rootReducer so that dispatching `resetAllState()` returns every slice
// to its initial state (including the RTK Query cache).
const resettableRootReducer = (
  state: ReturnType<typeof rootReducer> | undefined,
  action: UnknownAction,
): ReturnType<typeof rootReducer> => {
  if (action.type === RESET_ALL_STATE) {
    return rootReducer(undefined, action)
  }
  return rootReducer(state, action)
}

const persistConfig = {
  key: "root",
  storage,
  // `user` is persisted to survive page refreshes while logged in.
  // `campaignCreation` is persisted so an in-progress campaign draft survives
  // an accidental page refresh within the same session.
  // Both are cleared on logout via `persistor.purge()` in useUser.logout().
  whitelist: ["user", "campaignCreation"],
}

const persistedReducer = persistReducer(persistConfig, resettableRootReducer)

// Setup the store
export const store = setupStore()

// Type definitions
export type AppStore = typeof store
export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof rootReducer> & PersistPartial
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>

// Custom hooks for use throughout the app
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

// Function to configure the store
export function setupStore(preloadedState?: RootState) {
  return configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false, // Required for redux-persist
      })
        .concat(listenerMiddleware.middleware) // Append listenerMiddleware
        .concat(apiSlice.middleware), // Append apiSlice middleware
    preloadedState,
  })
}

export const persistor = persistStore(store)
